<template>
    <div class="form-group font-weight-normal field-filter">
        <template v-if="isForeignKey">
            <div class="select2-wrapper">
                <select2 :value="select2Value"
                         :plugin-options="searchOptions"
                         :is-ajax="true"
                         :disabled="selectNull"
                         @input="onSelect2"
                         class="form-control"></select2>
            </div>
        </template>
        <template v-else>
            <template v-if="type === 'string'">
                <input v-model="selectedValue"
                       :disabled="selectNull"
                       :maxlength="maxLength"
                       type="text"
                       class="form-control">
            </template>

            <template v-else-if="type === 'number'">
                <input v-numeric
                       v-model.number.trim="selectedValue"
                       :maxlength="maxLength"
                       type="text"
                       :disabled="selectNull"
                       class="form-control">
            </template>

            <template v-else-if="type === 'bool'">
                <select v-model="selectedValue"
                        :disabled="selectNull"
                        class="form-control">
                    <option :value="null" selected>{{ $t("common.select") }}</option>
                    <option :value="true">{{ $t("common.yes") }}</option>
                    <option :value="false">{{ $t("common.no") }}</option>
                </select>
            </template>

            <template v-else-if="type === 'date'">
                <template v-if="selectNull">
                    <input type="text"
                           :disabled="selectNull"
                           class="form-control">
                </template>
                <template v-else>
                    <flat-pickr class="form-control"
                                :is-date-time-picker="!isDateOnly"
                                :disabled="selectNull"
                                v-model="selectedValue"></flat-pickr>
                </template>
            </template>

            <template v-else-if="type === 'select'">
                <select v-model="selectedValue"
                        :disabled="selectNull"
                        class="form-control">
                    <option :value="null" selected>{{ $t("common.select") }}</option>
                    <option v-text="s.displayName" :key="s.value" :value="s.value" v-for="s in selectValues"></option>
                </select>
            </template>
        </template>

        <div v-if="showNullCheckbox">
            <label class="mb-0 position-relative">
                <input v-model="selectNull"
                       type="checkbox"
                       class="form-check-input ml-1"
                       :disabled="disableSelectNull">
                <span class="ml-4 mb-0">null</span>
            </label>
        </div>
    </div>
</template>

<script>
    import "@/vue";
    import i18n from "@/vue/localization";

    export default {
        i18n: i18n,

        props: {
            showNullCheckbox: {
                required: true,
                type: Boolean
            },

            type: {
                required: true,
                type: String
            },

            index: {
                required: true,
                type: Number
            },

            value: {
                required: false
            },

            fieldName: {
                required: true,
                type: String
            },

            columnName: {
                required: true,
                type: String
            },

            entityName: {
                required: true,
                type: String
            },

            maxLength: {
                required: false,
                type: Number
            },

            isNullable: {
                required: true,
                type: Boolean
            },

            selectValues: {
                required: true
            },

            isMultiline: {
                required: true,
                type: Boolean
            },

            isDateOnly: {
                required: true,
                type: Boolean
            },

            isForeignKey: {
                required: true,
                type: Boolean
            },

            isUrl: {
                required: true,
                type: Boolean
            },

            displayName: {
                required: true,
                type: String
            }
        },

        data() {
            return {
                selectedValue: this.value && this.value.selectedValue !== null ? this.value.selectedValue : null,
                selectNull: this.value && this.value.selectNull ? this.value.selectNull : false,

                ajaxSelectValue: this.isForeignKey && this.value && this.value.selectedValue !== null ? {
                    id: this.selectedValue,
                    displayName: this.displayName
                } : null,

                select2Text: this.displayName,
                select2mounted: false,
                searchOptions: {
                    minimumInputLength: 1,
                    allowClear: true,
                    width: "100%",
                    placeholder: "-",
                    ajax: {
                        url: this.url || "/api/EntityAdministration/Search",
                        delay: 250,
                        data: params => ({
                            term: params.term,
                            columnName: this.columnName,
                            entityName: this.entityName
                        }),
                        processResults: data => {
                            return {
                                results: data.map(item => ({
                                    id: item.id,
                                    text: item.displayName,
                                    data: item
                                }))
                            };
                        }
                    }
                }
            };
        },

        methods: {
            onSelect2(event) {
                if (event) {
                    this.selectedValue = event.id;
                    this.select2Text = event.text;
                    return;
                }

                if (this.select2mounted) {
                    this.selectedValue = null;
                }
            },

            createAjaxSelectParameters(input) {
                return {
                    term: input,
                    columnName: this.columnName,
                    entityName: this.entityName
                };
            },

            getValue() {
                return {
                    selectedValue: this.selectedValue,
                    selectNull: this.selectNull
                };
            }
        },

        mounted() {
            if (this.isForeignKey) {
                this.select2mounted = true;
            }
        },

        computed: {
            select2Value() {
                if (!this.isForeignKey || !this.value || this.value.selectedValue === null) {
                    return null;
                }

                return {
                    id: this.selectedValue,
                    text: this.select2Text
                };
            },

            disableSelectNull() {
                return !this.isNullable || this.selectedValue !== null;
            }
        },

        watch: {
            disableSelectNull() {
                if (this.disableSelectNull) {
                    this.selectNull = false;
                }
            },

            selectedValue() {
                if (this.selectedValue === "") {
                    this.selectedValue = null;
                }

                if (!this.value && this.selectedValue === null) {
                    return;
                }

                if (!this.value || this.selectedValue !== this.value.selectedValue) {
                    this.$emit("input", this.getValue());
                }
            },

            selectNull() {
                if (!this.value && this.selectNull === false) {
                    return;
                }

                if (!this.value || this.selectNull !== this.value.selectNull) {
                    this.$emit("input", this.getValue());
                }
            },

            ajaxSelectValue() {
                if (this.ajaxSelectValue === null) {
                    this.selectedValue = null;
                } else {
                    this.selectedValue = this.ajaxSelectValue.id;
                }
            },

            value() {
                if (!this.value) {
                    this.selectedValue = null;
                    this.selectNull = false;
                    this.ajaxSelectValue = null;
                    return;
                }

                if (this.value.selectNull !== this.selectNull) {
                    this.selectNull = this.value.selectNull;
                }

                if (this.value.selectedValue === undefined || this.value.selectedValue === null) {
                    this.selectedValue = null;
                } else if (this.value.selectedValue !== this.selectedValue) {
                    this.selectedValue = this.value.selectedValue;
                }
            }
        }
    };
</script>

<style>
    .field-filter .select2-wrapper {
        min-width: 150px;
    }
</style>